import { useMemo, useState, useEffect, useRef } from 'react';
import { useFocusById } from './useCareerFocus';
import { useRubric } from './useRubric';
import { useCareerCards } from './useCareerCards';
import { useContentLinks, ContentLink } from './useContentLinks';
import { useEvidenceConfig } from './useEvidenceConfig';
import { devLog } from '../utils/devLogger';
import { 
  calculateReviewPrepActivation,
  calculateRubricCoverage,
  generateRecommendations,
  calculateReviewPrepMetrics,
  RubricCoverageMetric,
  Recommendation,
  RubricProgress
} from '../utils/reviewPrepCalculations';

export interface ReviewPrepMetrics {
  // Timeline
  daysUntilEnd: number;
  isActive: boolean;
  activationDate: Date;
  
  // Coverage Analysis
  rubricCoverage: RubricCoverageMetric[];
  overallCoverageScore: number;
  
  // Activity Patterns
  cardsPerWeek: number;
  recentActivityLevel: 'high' | 'medium' | 'low';
  lastActiveDate: string | null;
  totalCards: number;
  
  // Recommendations
  recommendations: Recommendation[];
  criticalGaps: string[];
}

export const useInternshipReviewPrep = (focusId: string) => {
  const { focus, loading: focusLoading } = useFocusById(focusId);
  const { activeRubric, isLoading: rubricLoading } = useRubric(focusId);
  const { cards, loading: cardsLoading } = useCareerCards({ focusId });
  const { getFilteredLinks } = useContentLinks();
  const [contentLinks, setContentLinks] = useState<ContentLink[]>([]);
  const [linksLoading, setLinksLoading] = useState(false);
  const fetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Load evidence configuration
  const { config: evidenceConfig, loading: configLoading } = useEvidenceConfig(
    focusId, 
    activeRubric?.id
  );
  
  // Fetch content links for rubric items with debouncing
  useEffect(() => {
    // Clear any pending timeout
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }
    
    // Skip if no cards
    if (!cards || cards.length === 0) {
      setContentLinks([]);
      return;
    }
    
    let cancelled = false;
    
    const fetchContentLinks = async () => {
      setLinksLoading(true);
      try {
        // Get all links where career cards are linked to rubric criteria
        const links = await getFilteredLinks({
          sourceType: 'career_card',
          targetType: 'rubric_criterion'
        });
        
        if (!cancelled) {
          setContentLinks(links);
        }
      } catch (error) {
        devLog.error('Error fetching content links:', error);
        if (!cancelled) {
          setContentLinks([]);
        }
      } finally {
        if (!cancelled) {
          setLinksLoading(false);
        }
      }
    };
    
    // Debounce the fetch to prevent rapid re-fetches
    fetchTimeoutRef.current = setTimeout(() => {
      fetchContentLinks();
    }, 300); // 300ms debounce
    
    return () => {
      cancelled = true;
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [cards?.length, getFilteredLinks]); // Depend on cards length to reduce re-renders
  
  const loading = focusLoading || rubricLoading || cardsLoading || linksLoading || configLoading;
  
  // Calculate activation status and days until end
  const activationInfo = useMemo(() => {
    if (!focus?.start_date || !focus?.end_date) {
      return { isActive: false, activationDate: new Date(), daysUntilEnd: 0 };
    }
    return calculateReviewPrepActivation(focus.start_date, focus.end_date);
  }, [focus?.start_date, focus?.end_date]);
  
  // Transform rubric data into progress format
  const rubricProgress = useMemo(() => {
    if (!activeRubric) return [];
    
    const progress: RubricProgress[] = [];
    
    activeRubric.categories.forEach(category => {
      // Use criteria_items if available (new format with actual IDs)
      if (category.criteria_items && category.criteria_items.length > 0) {
        category.criteria_items.forEach(criterion => {
          progress.push({
            rubric_item_id: criterion.id,
            category: category.name,
            description: criterion.text || criterion.description || '',
            evidence_count: 0, // Will be calculated in coverage
            last_evidence_date: ''
          });
        });
      } else if (category.criteria && category.criteria.length > 0) {
        // Fallback for old format (shouldn't happen with current data)
        category.criteria.forEach((text, index) => {
          progress.push({
            rubric_item_id: `${category.id}-${index}`,
            category: category.name,
            description: text,
            evidence_count: 0,
            last_evidence_date: ''
          });
        });
      }
    });
    
    return progress;
  }, [activeRubric]);
  
  // Calculate rubric coverage metrics
  const rubricCoverage = useMemo(() => {
    if (!rubricProgress.length || !cards) return [];
    
    // Use loaded configuration or defaults
    const coverageConfig = {
      defaultEvidenceTarget: evidenceConfig.defaultEvidenceTarget,
      categoryTargets: evidenceConfig.categoryTargets,
      statusThresholds: evidenceConfig.statusThresholds
    };
    
    return calculateRubricCoverage(rubricProgress, cards, contentLinks, coverageConfig);
  }, [rubricProgress, cards, contentLinks, evidenceConfig]);
  
  // Generate smart recommendations
  const recommendations = useMemo(() => {
    if (!rubricCoverage.length) return [];
    return generateRecommendations(
      rubricCoverage, 
      activationInfo.daysUntilEnd, 
      evidenceConfig.timelineMilestones
    );
  }, [rubricCoverage, activationInfo.daysUntilEnd, evidenceConfig.timelineMilestones]);
  
  // Calculate additional metrics
  const additionalMetrics = useMemo(() => {
    if (!cards || !rubricCoverage.length) {
      return {
        cardsPerWeek: 0,
        recentActivityLevel: 'low' as const,
        overallCoverageScore: 0,
        criticalGaps: [],
        totalCards: 0,
        lastActiveDate: null
      };
    }
    return calculateReviewPrepMetrics(rubricCoverage, cards, activationInfo.daysUntilEnd);
  }, [rubricCoverage, cards, activationInfo.daysUntilEnd]);
  
  const metrics: ReviewPrepMetrics = {
    daysUntilEnd: activationInfo.daysUntilEnd,
    isActive: activationInfo.isActive,
    activationDate: activationInfo.activationDate,
    rubricCoverage,
    recommendations,
    ...additionalMetrics
  };
  
  return {
    metrics,
    loading,
    isReviewPeriod: activationInfo.isActive,
    isUrgent: activationInfo.daysUntilEnd <= 7,
    focus
  };
};